/**
 * Axios instance used by all feature hooks.
 *
 * Token source: Redux store (memory) — NEVER localStorage.
 * On authenticated-request 401, clears auth state and redirects to /login.
 * Bootstrap/logout requests opt out because their 401/transport failure is
 * handled by the auth flow itself.
 *
 * Import this directly in feature hooks/thunks:
 *   import apiClient from '../../shared/api/client';
 */

import axios from 'axios';

// In Docker: REACT_APP_API_URL is baked as '/api' at build time.
// '/api' is a relative path — the browser resolves it against the current
// origin (nginx on port 3000), which proxies to the backend container.
// This makes the app reachable from any IP without changing the bundle.
const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const apiClient = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true  // needed for httpOnly refresh cookie (Phase 3)
});

// ── STORE INJECTION ───────────────────────────────────────────────────────────
// Avoids circular imports: the store injects itself after creation.
let _store = null;

export function prepareRequestConfig(config) {
    // Let the browser generate the multipart boundary instead of serializing
    // the selected File as an empty object under the shared JSON default.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        delete config.headers['Content-Type'];
    }
    return config;
}

export function injectStore(store) {
    _store = store;
}

// ── REQUEST INTERCEPTOR ───────────────────────────────────────────────────────
apiClient.interceptors.request.use((config) => {
    prepareRequestConfig(config);
    if (_store) {
        const token = _store.getState().auth.token;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
});

// ── RESPONSE INTERCEPTOR ──────────────────────────────────────────────────────
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
            if (_store) {
                const { logout } = await import('../../features/auth/authSlice');
                _store.dispatch(logout());
            }
            // Avoid redirect loops on the login page
            if (!window.location.pathname.startsWith('/login')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export default apiClient;
